class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', (event) => {
        if (event.target.closest('price-range')) {
          const priceDebounce = debounce((e) => {
            this.onSubmitHandler(e);
          }, 1000);
          priceDebounce(event);
        } else {
          this.debouncedOnSubmit(event);
        }
      });
      
      facetForm.addEventListener('change', (event) => {
        if (event.target.tagName === 'SELECT' && (event.target.name === 'sort_by' || event.target.classList.contains('facet-filters__sort'))) {
          // Immediately submit when sort changes
          event.preventDefault();
          this.onSubmitHandler(event);
        }
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    FacetFiltersForm.initPaginationListeners();
  }
  
  static initPaginationListeners() {
    document.addEventListener('click', (event) => {
      const paginationLink = event.target.closest('.pagination__item a, .pagination__item--next, .pagination__item--prev');
      if (!paginationLink || paginationLink.classList.contains('pagination__item--current')) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      const href = paginationLink.getAttribute('href');
      if (!href) return;
      
      try {
        const currentUrl = new URL(window.location.href);
        const currentParams = new URLSearchParams(currentUrl.search);
        
        const linkUrl = href.startsWith('http') 
          ? new URL(href) 
          : new URL(href, window.location.origin);
        const linkParams = new URLSearchParams(linkUrl.search);
        
        let page = linkParams.get('page');
        if (!page) {
          // Try to extract page from href if not in params
          const pageMatch = href.match(/[?&]page=(\d+)/);
          if (pageMatch) {
            page = pageMatch[1];
          }
        }
        
        const combinedParams = new URLSearchParams();
        
        // Preserve all current parameters except page and section_id
        currentParams.forEach((value, key) => {
          if (key !== 'page' && key !== 'section_id') {
            // Handle multiple values for the same key (e.g., multiple filter selections)
            if (combinedParams.has(key)) {
              combinedParams.append(key, value);
            } else {
              combinedParams.set(key, value);
            }
          }
        });
        
        // Add page parameter
        if (page) {
          combinedParams.set('page', page);
        }
        
        const combinedParamsString = combinedParams.toString();
        
        FacetFiltersForm.renderPage(combinedParamsString, event);
        
        const productGrid = document.getElementById('ProductGridContainer');
        if (productGrid) {
          productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        console.error('Error handling pagination click:', error);
        window.location.href = href;
      }
    }, true);
  }
  
  static updatePaginationLinks() {
    const currentUrl = new URL(window.location.href);
    const currentParams = new URLSearchParams(currentUrl.search);
    
    const filterParams = new URLSearchParams();
    // Preserve all parameters except page and section_id
    currentParams.forEach((value, key) => {
      if (key !== 'page' && key !== 'section_id') {
        // Handle multiple values for the same key
        if (filterParams.has(key)) {
          filterParams.append(key, value);
        } else {
          filterParams.set(key, value);
        }
      }
    });
    
    const filterParamString = filterParams.toString();
    
    document.querySelectorAll('.pagination__item a').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      
      try {
        const linkUrl = href.startsWith('http') 
          ? new URL(href) 
          : new URL(href, window.location.origin);
        const linkParams = new URLSearchParams(linkUrl.search);
        
        let page = linkParams.get('page');
        if (!page) {
          // Try to extract page from href if not in params
          const pageMatch = href.match(/[?&]page=(\d+)/);
          if (pageMatch) {
            page = pageMatch[1];
          }
        }
        
        const newParams = new URLSearchParams(filterParamString);
        if (page) {
          newParams.set('page', page);
        }
        
        const basePath = window.location.pathname;
        const newHref = `${basePath}?${newParams.toString()}`;
        link.setAttribute('href', newHref);
      } catch (e) {
        console.error('Error updating pagination link:', e);
      }
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return;
    
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collectionElement = productGridContainer.querySelector('.collection');
      if (collectionElement) {
        collectionElement.classList.add('loading');
      }
    }
    
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        
        // Fix for scroll animation trigger
        if (typeof initializeScrollAnimationTrigger === 'function') {
          try {
            const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
            const productGridContainer = parsedHTML.getElementById('ProductGridContainer');
            if (productGridContainer) {
              initializeScrollAnimationTrigger(productGridContainer);
            }
          } catch (err) {
            console.log('Scroll animation trigger skipped');
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching filter data:', error);
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;
    
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newProductGridContainer = parsedHTML.getElementById('ProductGridContainer');
    if (newProductGridContainer) {
      productGridContainer.innerHTML = newProductGridContainer.innerHTML;

      productGridContainer
        .querySelectorAll('.scroll-trigger')
        .forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
      
      // Update pagination links after a short delay to ensure DOM is ready
      setTimeout(() => {
        FacetFiltersForm.updatePaginationLinks();
      }, 100);
      
      // Ensure sort select reflects current URL state
      const currentUrl = new URL(window.location.href);
      const currentSortBy = currentUrl.searchParams.get('sort_by');
      if (currentSortBy) {
        const sortSelects = document.querySelectorAll('select[name="sort_by"]');
        sortSelects.forEach((select) => {
          if (select.value !== currentSortBy) {
            select.value = currentSortBy;
          }
        });
      }
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const productCountElement = parsedHTML.getElementById('ProductCount');
    if (!productCountElement) return;
    
    const count = productCountElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    // Include all filter form selectors (desktop, original mobile, new mobile filter)
    const filterSelectors = '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersFormMobileFilter .js-filter, #FacetFiltersPillsForm .js-filter';
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(filterSelectors);
    // Only update elements in the original forms, not the new mobile filter (it will be synced separately)
    const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      if (!event || !event.target) return false;
      const jsFilter = event.target.closest('.js-filter');
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          const previousElement = document.getElementById(previousElementId);
          if (previousElement && elementToRender.className === previousElementClassName) {
            previousElement.after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentElement = document.getElementById(elementToRender.parentElement.id);
          if (parentElement) {
            const firstJsFilter = parentElement.querySelector('.js-filter');
            if (firstJsFilter) {
              firstJsFilter.before(elementToRender);
            } else {
              parentElement.appendChild(elementToRender);
            }
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    
    // Sync filter content to new mobile filter drawer
    FacetFiltersForm.syncMobileFilterDrawer(parsedHTML);

    if (countsToRender && event && event.target) {
      const jsFilter = event.target.closest('.js-filter');
      if (jsFilter && jsFilter.id) {
        FacetFiltersForm.renderCounts(countsToRender, jsFilter);
        const closestJSFilterElement = document.getElementById(jsFilter.id);
        if (closestJSFilterElement) {
          FacetFiltersForm.renderMobileCounts(countsToRender, closestJSFilterElement);

          const newElementSelector = closestJSFilterElement.classList.contains('mobile-facets__details')
            ? `.mobile-facets__close-button`
            : `.facets__summary`;
          const newElementToActivate = closestJSFilterElement.querySelector(newElementSelector);

          const isTextInput = event.target.getAttribute('type') === 'text';

          if (newElementToActivate && !isTextInput) newElementToActivate.focus();
        }
      }
    }
  }

  static syncMobileFilterDrawer(parsedHTML) {
    // Sync checkbox states from server response to the new filter drawer
    const sourceForm = parsedHTML.getElementById('FacetFiltersFormMobile');
    const targetForm = document.getElementById('FacetFiltersFormMobileFilter');
    
    if (sourceForm && targetForm) {
      // Sync checkbox checked states
      sourceForm.querySelectorAll('input[type="checkbox"]').forEach((sourceCheckbox) => {
        const name = sourceCheckbox.getAttribute('name');
        const value = sourceCheckbox.getAttribute('value');
        if (name && value) {
          const targetCheckbox = targetForm.querySelector(`input[type="checkbox"][name="${name}"][value="${value}"]`);
          if (targetCheckbox) {
            targetCheckbox.checked = sourceCheckbox.checked;
            // Also update the label active state
            const sourceLabel = sourceCheckbox.closest('.mobile-facets__label');
            const targetLabel = targetCheckbox.closest('.mobile-facets__label');
            if (sourceLabel && targetLabel) {
              targetLabel.classList.toggle('active', sourceCheckbox.checked);
            }
          }
        }
      });
      
      // Sync facet counts
      sourceForm.querySelectorAll('.facet-checkbox__text').forEach((sourceText) => {
        const label = sourceText.closest('.mobile-facets__label');
        if (label) {
          const checkbox = label.querySelector('input[type="checkbox"]');
          if (checkbox) {
            const name = checkbox.getAttribute('name');
            const value = checkbox.getAttribute('value');
            const targetLabel = targetForm.querySelector(`input[type="checkbox"][name="${name}"][value="${value}"]`)?.closest('.mobile-facets__label');
            if (targetLabel) {
              const targetText = targetLabel.querySelector('.facet-checkbox__text');
              if (targetText) {
                targetText.innerHTML = sourceText.innerHTML;
              }
            }
          }
        }
      });
      
      // Sync disabled states
      sourceForm.querySelectorAll('.mobile-facets__label').forEach((sourceLabel) => {
        const checkbox = sourceLabel.querySelector('input[type="checkbox"]');
        if (checkbox) {
          const name = checkbox.getAttribute('name');
          const value = checkbox.getAttribute('value');
          const targetLabel = targetForm.querySelector(`input[type="checkbox"][name="${name}"][value="${value}"]`)?.closest('.mobile-facets__label');
          if (targetLabel) {
            const isDisabled = sourceLabel.classList.contains('disabled');
            targetLabel.classList.toggle('disabled', isDisabled);
            const targetCheckbox = targetLabel.querySelector('input[type="checkbox"]');
            if (targetCheckbox) {
              targetCheckbox.disabled = isDisabled;
            }
          }
        }
      });
      
      // Sync price range values
      const sourcePriceInputs = sourceForm.querySelectorAll('input[name*="filter.v.price"]');
      sourcePriceInputs.forEach((sourceInput) => {
        const name = sourceInput.getAttribute('name');
        const targetInput = targetForm.querySelector(`input[name="${name}"]`);
        if (targetInput) {
          targetInput.value = sourceInput.value;
        }
      });
    }
    
    // Update sort radio buttons to reflect current URL state
    const currentUrl = new URL(window.location.href);
    const currentSortBy = currentUrl.searchParams.get('sort_by');
    document.querySelectorAll('#FacetFiltersFormMobileSort input[type="radio"][name="sort_by"]').forEach((radio) => {
      const isSelected = currentSortBy ? (radio.value === currentSortBy) : (radio.value === '');
      radio.checked = isSelected;
      const parent = radio.closest('.mobile-sort-option');
      if (parent) {
        parent.classList.toggle('active', isSelected);
      }
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const domElement = document.querySelector(selector);
      if (domElement) {
        domElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const htmlElement = html.querySelector(selector);
      const domElement = document.querySelector(selector);
      if (!htmlElement || !domElement) return;
      domElement.innerHTML = htmlElement.innerHTML;
    });

    // Re-bind events for all mobile filter/sort forms
    const mobileFormIds = ['FacetFiltersFormMobile', 'FacetFiltersFormMobileFilter', 'FacetFiltersFormMobileSort'];
    mobileFormIds.forEach((formId) => {
      const formElement = document.getElementById(formId);
      if (formElement) {
        const menuDrawer = formElement.closest('menu-drawer');
        if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
          menuDrawer.bindEvents();
        }
      }
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
    history.pushState({ searchParams }, '', url);
    FacetFiltersForm.updatePaginationLinks();
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid || !productGrid.dataset.id) {
      const filtersElement = document.getElementById('main-collection-filters');
      if (filtersElement && filtersElement.dataset.id) {
        return [{ section: filtersElement.dataset.id }];
      }
      const productGridContainer = document.getElementById('ProductGridContainer');
      if (productGridContainer) {
        const sectionId = productGridContainer.closest('[data-section-id]')?.dataset?.sectionId ||
                          productGridContainer.closest('section')?.id?.replace('shopify-section-', '');
        if (sectionId) {
          return [{ section: sectionId }];
        }
      }
      return [];
    }
    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    const checkedSortRadio = form.querySelector('input[type="radio"][name="sort_by"]:checked');

    for (const [key, value] of formData.entries()) {
      if (value === '') {
        continue;
      }

      if (key === 'sort_by') {
        if (checkedSortRadio) {
          params.set('sort_by', checkedSortRadio.value);
        } else if (!params.has('sort_by')) {
          params.set('sort_by', value);
        }
        continue;
      }

      if (key.includes('filter.v.price')) {
        const cleanedValue = value.toString().replace(/[^\d.]/g, '');
        if (cleanedValue) {
          params.append(key, cleanedValue);
        }
      } else {
        params.append(key, value);
      }
    }

    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const target = event.target || event.srcElement;
    const isMobileCheckbox = target.classList && target.classList.contains('mobile-facets__checkbox');
    
    if (isMobileCheckbox) {
      const form = target.closest('form');
      if (form) {
        const searchParams = this.createSearchParams(form);
        this.onSubmitForm(searchParams, event);
      }
    } else {
      const forms = [];
      const closestForm = target.closest('form');
      // Check for all mobile form IDs
      const mobileFormIds = ['FacetFiltersFormMobile', 'FacetFiltersFormMobileFilter', 'FacetFiltersFormMobileSort'];
      const isMobile = closestForm && mobileFormIds.includes(closestForm.id);

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          // Desktop forms
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const params = this.createSearchParams(form);
            if (params) {
              forms.push(params);
            }
          }
        } else {
          // Mobile forms - use the closest form that triggered the event
          if (closestForm && form.id === closestForm.id) {
            const params = this.createSearchParams(form);
            if (params) {
              forms.push(params);
            }
          }
        }
      });
      
      if (forms.length > 0) {
        this.onSubmitForm(forms.join('&'), event);
      }
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

window.applySortOptionClick = function (sortOption, event) {
  if (!sortOption) return;

  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  const radio = sortOption.querySelector('input[type="radio"][name="sort_by"]');
  if (!radio) return;

  radio.checked = true;

  const form = sortOption.closest('form');
  if (form) {
    const select = form.querySelector('select[name="sort_by"], select.facet-filters__sort');
    if (select) {
      select.value = radio.value;
    }
  }

  sortOption
    .closest('.sort-dropdown-panel, .mobile-facets__sort-options')
    ?.querySelectorAll('.sort-option, .mobile-sort-option')
    .forEach((item) => {
      item.classList.toggle('active', item === sortOption);
    });

  const facetFiltersForm = sortOption.closest('facet-filters-form');
  if (facetFiltersForm?.onSubmitHandler) {
    facetFiltersForm.onSubmitHandler.call(facetFiltersForm, {
      target: radio,
      preventDefault: () => {},
    });
  }

  sortOption.closest('.sort-dropdown-custom')?.removeAttribute('open');

  const drawer = sortOption.closest('.mobile-facets__wrapper--sort');
  if (drawer) {
    setTimeout(() => drawer.querySelector('summary')?.click(), 300);
  }
};

// Update pagination links on page load to preserve filters/sort
const updatePaginationOnLoad = () => {
  // Small delay to ensure DOM is fully ready
  setTimeout(() => {
    FacetFiltersForm.updatePaginationLinks();
  }, 200);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updatePaginationOnLoad);
} else {
  updatePaginationOnLoad();
}

// Also update pagination links after AJAX content loads
const paginationObserver = new MutationObserver(() => {
  const paginationLinks = document.querySelectorAll('.pagination__item a');
  if (paginationLinks.length > 0) {
    FacetFiltersForm.updatePaginationLinks();
  }
});

const productGridContainer = document.getElementById('ProductGridContainer');
if (productGridContainer) {
  paginationObserver.observe(productGridContainer, {
    childList: true,
    subtree: true
  });
}

// Close sort dropdown when clicking outside (same behavior as other filters)
document.addEventListener('click', (event) => {
  document.querySelectorAll('.sort-dropdown-custom[open]').forEach((dropdown) => {
    // Check if click was outside the dropdown
    if (!dropdown.contains(event.target)) {
      dropdown.removeAttribute('open');
    }
  });
});

// Also close sort dropdown on focusout (like DetailsDisclosure)
document.addEventListener('focusout', (event) => {
  document.querySelectorAll('.sort-dropdown-custom[open]').forEach((dropdown) => {
    setTimeout(() => {
      if (!dropdown.contains(document.activeElement)) {
        dropdown.removeAttribute('open');
      }
    });
  });
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.debouncedSubmit = debounce((event) => {
      this.submitForm(event);
    }, 1000);
    
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('input', this.onInputChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
      element.addEventListener('blur', this.onBlur.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.submitForm(event);
  }

  onInputChange(event) {
    this.debouncedSubmit(event);
  }

  onBlur(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.submitForm(event);
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
    
    if (event.key === 'Enter') {
      event.preventDefault();
      this.adjustToValidValues(event.currentTarget);
      this.setMinAndMaxValues();
      this.submitForm(event);
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput && maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput && minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput && minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput && maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min')) || 0;
    const max = Number(input.getAttribute('data-max')) || Infinity;

    if (value < min) input.value = min;
    if (value > max && max !== Infinity) input.value = max;
  }

  submitForm(event) {
    const form = this.closest('form');
    if (!form) {
      const facetForm = this.closest('facet-filters-form');
      if (facetForm) {
        form = facetForm.querySelector('form');
      }
    }
    if (!form) return;
    
    const inputs = this.querySelectorAll('input');
    inputs.forEach((input) => {
      if (input.value) {
        let cleanedValue = input.value.toString().replace(/[^\d.]/g, '');
        const parts = cleanedValue.split('.');
        if (parts.length > 2) {
          cleanedValue = parts[0] + '.' + parts.slice(1).join('');
        }
        input.value = cleanedValue;
      }
    });
    
    const facetFiltersForm = form.closest('facet-filters-form');
    if (facetFiltersForm && typeof facetFiltersForm.onSubmitHandler === 'function') {
      const syntheticEvent = {
        target: inputs[0] || event.target,
        preventDefault: () => {},
        stopPropagation: () => {}
      };
      facetFiltersForm.onSubmitHandler(syntheticEvent);
    }
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (facetLink) {
      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
      });
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) {
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define('facet-remove', FacetRemove);